import React from 'react';
import Icon from '../../../components/AppIcon';

const SubscriptionOverviewCard = ({ subscription, tiers, daysUntilBilling, organizationType = 'private' }) => {
  const getTierDisplay = (tierName) => {
    const tier = tiers?.find(t => t?.tierName === tierName);
    return tier?.displayName || tierName;
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'active': 'text-success bg-success/10',
      'suspended': 'text-warning bg-warning/10',
      'cancelled': 'text-error bg-error/10'
    };
    return statusColors?.[status] || 'text-muted-foreground bg-muted';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-lg shadow-sm border border-border p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-1">Current Subscription</h2>
          <p className="text-sm text-muted-foreground">{subscription?.companyName}</p>
        </div>
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(subscription?.subscriptionStatus)}`}>
          <Icon name="CheckCircle" size={14} className="mr-1" />
          {subscription?.subscriptionStatus?.toUpperCase()}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-card rounded-lg p-4 border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Users" size={20} className="text-primary" />
            <span className="text-2xl font-bold text-foreground">{subscription?.activeEmployeeCount}</span>
          </div>
          <p className="text-sm text-muted-foreground">Active Employees</p>
        </div>

        <div className="bg-card rounded-lg p-4 border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="TrendingUp" size={20} className="text-secondary" />
            <span className="text-lg font-bold text-foreground">{getTierDisplay(subscription?.currentTier)}</span>
          </div>
          <p className="text-sm text-muted-foreground">Current Tier</p>
        </div>

        <div className="bg-card rounded-lg p-4 border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="DollarSign" size={20} className="text-accent" />
            <span className="text-2xl font-bold text-foreground">K{subscription?.monthlyPriceKina}</span>
          </div>
          <p className="text-sm text-muted-foreground">Monthly Price</p>
        </div>

        <div className="bg-card rounded-lg p-4 border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Calendar" size={20} className="text-primary" />
            <span className="text-sm font-bold text-foreground">{daysUntilBilling} days</span>
          </div>
          <p className="text-sm text-muted-foreground">Until Next Billing</p>
        </div>
      </div>

      <div className="bg-card rounded-lg p-4 border border-border">
        <h3 className="text-sm font-semibold text-foreground mb-3">💡 Tiered Pricing Model for PNG HR Compliance App</h3>
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs text-muted-foreground">
            {organizationType === 'public' ? 'Public Sector Organizations - Invoice-based billing' : 'Private Companies - Automated billing'}
          </span>
          <a 
            href="mailto:admin@hrpacificrbs.com" 
            className="text-xs text-primary hover:underline flex items-center"
          >
            <Icon name="Mail" size={12} className="mr-1" />
            Contact for details
          </a>
        </div>
        
        <div className="space-y-4">
          {tiers?.filter(tier => organizationType === 'public' ? tier?.tierName === 'enterprise' : tier?.tierName !== 'enterprise')?.map((tier) => {
            const isCurrentTier = tier?.tierName === subscription?.currentTier;
            return (
              <div
                key={tier?.id}
                className={`p-4 rounded-lg border ${
                  isCurrentTier
                    ? 'border-primary bg-primary/5' : 'border-border bg-background'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-base font-bold text-foreground">{tier?.displayName}</h4>
                      {isCurrentTier && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary text-primary-foreground">
                          <Icon name="CheckCircle" size={12} className="mr-1" />
                          Current
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mb-1">{tier?.targetUsers}</p>
                    <p className="text-xs text-muted-foreground italic">{tier?.valueProposition}</p>
                  </div>
                  <div className="text-right ml-4">
                    {tier?.tierName === 'enterprise' ? (
                      <div>
                        <p className="text-lg font-bold text-foreground">Custom</p>
                        <p className="text-xs text-muted-foreground">Via Invoice</p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-lg font-bold text-foreground">
                          K{tier?.monthlyPriceKinaMin}–{tier?.monthlyPriceKinaMax}
                        </p>
                        <p className="text-xs text-muted-foreground">per month</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs font-medium text-foreground mb-2">Features:</p>
                  <ul className="space-y-1">
                    {tier?.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-xs text-muted-foreground">
                        <Icon name="Check" size={12} className="text-success mr-2 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {tier?.tierName !== 'enterprise' && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-xs text-muted-foreground">
                      <Icon name="Users" size={12} className="inline mr-1" />
                      {tier?.minEmployees}–{tier?.maxEmployees || '∞'} staff
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {organizationType === 'public' && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <Icon name="Info" size={16} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-800">
                <strong>Public Sector Note:</strong> Due to the large number of employees in government sectors,
invoices are issued via email. Please contact us at <a href="mailto:admin@hrpacificrbs.com" className="underline font-medium">admin@hrpacificrbs.com</a> for
                custom pricing and payment arrangements.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionOverviewCard;